import React, { useState } from 'react';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Toast } from './ui/Toast';
import ConfirmDialog from './ConfirmDialog';
import { Input } from './ui/input';
import { Label } from './ui/Label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { 
  ArrowLeft, Award, BookOpen, Plus, Edit, Trash2, Save, X, 
  RotateCcw, Settings, Eye, Sliders, Palette, Hash, Type
} from 'lucide-react';
import useAppreciationsConfig from '../hooks/useAppreciationsConfig';

// Palette de couleurs disponibles
const COULEURS_DISPONIBLES = [
  { value: 'bg-emerald-100 text-emerald-700', label: 'Émeraude', dot: 'bg-emerald-500' },
  { value: 'bg-green-100 text-green-700', label: 'Vert', dot: 'bg-green-500' },
  { value: 'bg-teal-100 text-teal-700', label: 'Teal', dot: 'bg-teal-500' },
  { value: 'bg-cyan-100 text-cyan-700', label: 'Cyan', dot: 'bg-cyan-500' },
  { value: 'bg-blue-100 text-blue-700', label: 'Bleu', dot: 'bg-blue-500' },
  { value: 'bg-indigo-100 text-indigo-700', label: 'Indigo', dot: 'bg-indigo-500' },
  { value: 'bg-purple-100 text-purple-700', label: 'Violet', dot: 'bg-purple-500' },
  { value: 'bg-pink-100 text-pink-700', label: 'Rose', dot: 'bg-pink-500' },
  { value: 'bg-orange-100 text-orange-600', label: 'Orange', dot: 'bg-orange-500' },
  { value: 'bg-amber-100 text-amber-700', label: 'Ambre', dot: 'bg-amber-500' },
  { value: 'bg-red-100 text-red-600', label: 'Rouge', dot: 'bg-red-500' },
  { value: 'bg-gray-100 text-gray-700', label: 'Gris', dot: 'bg-gray-500' },
];

const ConfigAppreciations = ({ onBackToDashboard, embedded = false }) => {
  const {
    appreciationsResultat,
    appreciationsNote,
    loading,
    creer,
    modifier,
    supprimer,
    reinitialiser,
  } = useAppreciationsConfig();

  const [activeTab, setActiveTab] = useState('resultat');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    type: 'resultat', libelle: '', note_min: '', note_max: '',
    couleur: 'bg-blue-100 text-blue-700', ordre: 0
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', description: '', type: 'warning',
    confirmText: 'Confirmer', onConfirm: () => {}, isLoading: false
  });

  const currentData = activeTab === 'resultat' ? appreciationsResultat : appreciationsNote;
  const accentColor = activeTab === 'resultat' ? '#246990' : '#319190';

  // ========================================
  // HANDLERS
  // ========================================
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const openAddForm = () => {
    setEditingItem(null);
    setFormData({
      type: activeTab, libelle: '', note_min: '', note_max: '',
      couleur: 'bg-blue-100 text-blue-700', ordre: currentData.length + 1
    });
    setShowForm(true);
  };

  const openEditForm = (item) => {
    setEditingItem(item);
    setFormData({
      type: item.type, libelle: item.libelle,
      note_min: item.note_min, note_max: item.note_max,
      couleur: item.couleur || 'bg-blue-100 text-blue-700', ordre: item.ordre
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.libelle.trim()) { showToast('Le libellé est obligatoire', 'error'); return; }
    if (formData.note_min === '' || formData.note_max === '') { showToast('Les seuils min et max sont obligatoires', 'error'); return; }
    if (parseFloat(formData.note_min) > parseFloat(formData.note_max)) { showToast('Le seuil min ne peut pas dépasser le max', 'error'); return; }

    try {
      setSaving(true);
      const result = editingItem
        ? await modifier(editingItem.id, formData)
        : await creer(formData);

      if (result.success) {
        showToast(editingItem ? 'Appréciation modifiée avec succès' : 'Appréciation créée avec succès');
        setShowForm(false);
      } else {
        showToast(result.error || 'Erreur lors de la sauvegarde', 'error');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequest = (item) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Supprimer cette appréciation ?',
      description: `L'appréciation "${item.libelle}" (${item.note_min} — ${item.note_max}) sera désactivée. Cette action est réversible.`,
      type: 'warning',
      confirmText: 'Supprimer',
      isLoading: false,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        try {
          const result = await supprimer(item.id);
          if (result.success) {
            showToast('Appréciation supprimée');
            setConfirmDialog(prev => ({ ...prev, isOpen: false, isLoading: false }));
          } else {
            showToast(result.error || 'Erreur', 'error');
            setConfirmDialog(prev => ({ ...prev, isLoading: false }));
          }
        } catch (err) {
          showToast(err.message, 'error');
          setConfirmDialog(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const handleResetRequest = () => {
    const typeLabel = activeTab === 'resultat' ? 'Résultat' : 'Notes';
    setConfirmDialog({
      isOpen: true,
      title: 'Réinitialiser les appréciations ?',
      description: `Toutes les appréciations de type "${typeLabel}" seront remplacées par les valeurs par défaut du système. Les données actuelles seront perdues.`,
      type: 'warning',
      confirmText: 'Réinitialiser',
      isLoading: false,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        try {
          const result = await reinitialiser(activeTab);
          if (result.success) {
            showToast(`Appréciations "${typeLabel}" réinitialisées`);
            setConfirmDialog(prev => ({ ...prev, isOpen: false, isLoading: false }));
          } else {
            showToast(result.error || 'Erreur', 'error');
            setConfirmDialog(prev => ({ ...prev, isLoading: false }));
          }
        } catch (err) {
          showToast(err.message, 'error');
          setConfirmDialog(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  // ========================================
  // RENDER TABLE CONTENT (shared between tabs)
  // ========================================
  const renderTableContent = () => (
    <div className="bg-white rounded-xl border border-gray-200/80 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04)]">

      {/* Toolbar */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-100">
            <Settings size={14} className="text-gray-400" />
          </div>
          <div>
            <span className="font-semibold text-gray-700 text-[13px]">
              {currentData.length} appréciation{currentData.length > 1 ? 's' : ''}
            </span>
            <span className="text-xs text-gray-400 ml-1.5">
              configurée{currentData.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleResetRequest}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all">
            <RotateCcw size={13} /> Réinitialiser
          </button>
          <button onClick={openAddForm}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg shadow-sm transition-all hover:shadow-md"
            style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)` }}>
            <Plus size={14} /> Ajouter
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-gray-200"
            style={{ borderTopColor: accentColor }} />
          <p className="text-sm text-gray-400 font-medium">Chargement des appréciations…</p>
        </div>
      ) : (
        <>
          {/* Empty state */}
          {currentData.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100">
                <Award size={28} className="text-gray-300" />
              </div>
              <p className="text-gray-700 font-semibold mb-1">Aucune appréciation configurée</p>
              <p className="text-gray-400 text-sm">Cliquez sur "Ajouter" ou "Réinitialiser" pour commencer</p>
            </div>
          ) : (
            <div>
              {/* En-tête tableau */}
              <div className="grid grid-cols-12 gap-4 px-5 py-2.5 bg-gray-50/80 border-b border-gray-100">
                <div className="col-span-1 text-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">#</div>
                <div className="col-span-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Libellé</div>
                <div className="col-span-2 text-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">Min</div>
                <div className="col-span-2 text-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">Max</div>
                <div className="col-span-2 text-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">Badge</div>
                <div className="col-span-1" />
              </div>

              {/* Lignes */}
              {currentData.map((item) => (
                <div key={item.id}
                  className="grid grid-cols-12 gap-4 px-5 py-3.5 items-center border-b border-gray-50 hover:bg-gray-50/60 transition-all group"
                >
                  {/* Ordre */}
                  <div className="col-span-1 text-center">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold"
                      style={{ backgroundColor: `${accentColor}10`, color: accentColor }}>
                      {item.ordre}
                    </span>
                  </div>

                  {/* Libellé */}
                  <div className="col-span-4 flex items-center gap-2.5">
                    <div className="w-1 h-6 rounded-full flex-shrink-0"
                      style={{ backgroundColor: accentColor, opacity: 0.3 }} />
                    <span className="font-semibold text-gray-800 text-sm">{item.libelle}</span>
                  </div>

                  {/* Note min */}
                  <div className="col-span-2 text-center">
                    <span className="text-sm font-mono font-semibold text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100 inline-block min-w-[60px]">
                      {parseFloat(item.note_min).toFixed(2)}
                    </span>
                  </div>

                  {/* Note max */}
                  <div className="col-span-2 text-center">
                    <span className="text-sm font-mono font-semibold text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100 inline-block min-w-[60px]">
                      {parseFloat(item.note_max).toFixed(2)}
                    </span>
                  </div>

                  {/* Aperçu badge */}
                  <div className="col-span-2 text-center">
                    <span className={`text-[11px] px-3 py-1.5 rounded-full font-semibold inline-block ${item.couleur || 'bg-gray-100 text-gray-700'}`}>
                      {item.libelle}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all">
                    <button onClick={() => openEditForm(item)}
                      className="p-1.5 text-gray-400 hover:text-[#246990] hover:bg-blue-50 rounded-lg transition-all"
                      title="Modifier">
                      <Edit size={14} />
                    </button>
                    <button onClick={() => handleDeleteRequest(item)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      title="Supprimer">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Barre visuelle des seuils */}
          {currentData.length > 0 && (
            <div className="px-5 py-5 bg-gray-50/50 border-t border-gray-100">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-6 h-6 bg-white rounded-md flex items-center justify-center border border-gray-100">
                  <Eye size={12} className="text-gray-400" />
                </div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  Visualisation des seuils sur 20
                </p>
              </div>

              <div className="relative">
                <div className="flex rounded-xl overflow-hidden h-10 shadow-[inset_0_1px_3px_rgba(0,0,0,0.08)] border border-gray-200/80 bg-gray-100">
                  {[...currentData]
                    .sort((a, b) => parseFloat(a.note_min) - parseFloat(b.note_min))
                    .map((item) => {
                      const width = ((parseFloat(item.note_max) - parseFloat(item.note_min)) / 20) * 100;
                      return (
                        <div key={item.id}
                          className={`flex items-center justify-center text-xs font-semibold border-r border-white/40 last:border-r-0 transition-all hover:opacity-90 ${item.couleur || 'bg-gray-200 text-gray-600'}`}
                          style={{ width: `${Math.max(width, 5)}%` }}
                          title={`${item.libelle}: ${parseFloat(item.note_min).toFixed(1)} — ${parseFloat(item.note_max).toFixed(1)}`}
                        >
                          <span className="truncate px-1.5 text-[11px] font-bold">{item.libelle}</span>
                        </div>
                      );
                    })}
                </div>
                <div className="flex justify-between mt-2 px-1">
                  {[0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20].map(n => (
                    <div key={n} className="flex flex-col items-center">
                      <div className="w-px h-1.5 bg-gray-300 mb-0.5" />
                      <span className="text-[9px] text-gray-400 font-medium">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Le primaire se note sur 10 (décision du 7/10/2026) ; ces
                  tranches, sans section, servent à toute l'école : bulletin,
                  carnet, statistiques et outil décisionnel y ramènent une note
                  du primaire en la doublant. */}
              <p className="mt-3 text-xs text-gray-500">
                Ces seuils valent pour toute l'école. Le primaire est noté sur 10 : une note ou une moyenne
                du primaire est doublée avant d'être comparée (7,5/10 se lit comme 15/20).
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );

  // ========================================
  // MAIN RENDER
  // ========================================
  return (
    <div className="w-full">
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        isLoading={confirmDialog.isLoading}
      />

      {/* Header standalone */}
      {!embedded && (
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <Button onClick={onBackToDashboard} variant="outline" size="sm"
              className="border-gray-200 text-gray-500 hover:bg-[#246990] hover:text-white hover:border-[#246990] transition-all">
              <ArrowLeft size={15} className="mr-1.5" /> Retour
            </Button>
            <div className="h-8 w-px bg-gray-200" />
            <div className="w-10 h-10 bg-gradient-to-br from-[#246990] to-[#1a4f6e] rounded-xl flex items-center justify-center shadow-sm">
              <Sliders className="text-white" size={18} />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Configuration des Appréciations</h1>
              <p className="text-xs text-gray-500 mt-0.5">Définissez les seuils et libellés pour les résultats et les notes</p>
            </div>
          </div>
        </div>
      )}

      {/* Section Header embedded */}
      {embedded && (
        <div className="flex items-center gap-3 pb-4 border-b border-gray-100 mb-5">
         
          <div>
            <h2 className="text-[15px] font-bold text-gray-900">Configuration des Appréciations</h2>
            <p className="text-xs text-gray-500 mt-0.5">Définissez les seuils et libellés pour les résultats et les notes</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="resultat" onValueChange={(val) => { setActiveTab(val); setShowForm(false); }} className="w-full">
        <TabsList className="grid grid-cols-2 mb-5 bg-gray-100/80 p-1 rounded-xl">
          <TabsTrigger
            value="resultat"
            className="data-[state=active]:bg-[#246990] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <span>Décision</span>
            <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded-md bg-white/20 font-bold">
              {appreciationsResultat.length}
            </span>
          </TabsTrigger>
          <TabsTrigger
            value="note"
            className="data-[state=active]:bg-[#319190] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
         
            <span>Appréciations des Notes</span>
            <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded-md bg-white/20 font-bold">
              {appreciationsNote.length}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="resultat" className="mt-0 focus-visible:outline-none">
          {renderTableContent()}
        </TabsContent>

        <TabsContent value="note" className="mt-0 focus-visible:outline-none">
          {renderTableContent()}
        </TabsContent>
      </Tabs>

      {/* Le modal etait un calque monte a la main, avec sa propre animation
          `modalSlideIn` definie nulle part ailleurs dans l'application : il
          s'ouvrait autrement que tous les autres. Il passe sur les primitives
          `Dialog`, qui apportent l'animation commune.

          CLAUDE.md l'impose aussi pour une raison technique : Radix pose
          `pointer-events: none` sur le body tant qu'un Dialog est ouvert, et un
          overlay maison rendu hors de son portail s'affiche normalement mais ne
          recoit aucun clic. */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-white"
              style={{ backgroundColor: accentColor }}
            >
              {editingItem ? <Edit size={18} /> : <Plus size={18} />}
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editingItem ? "Modifier l'appréciation" : 'Nouvelle appréciation'}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                {activeTab === 'resultat'
                  ? 'Appréciation de résultat, utilisée dans les statistiques'
                  : 'Appréciation de note, imprimée sur les bulletins'}
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label className="text-sm text-gray-700">Libellé *</Label>
                <Input
                  value={formData.libelle}
                  onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                  placeholder="Admis(e), Excellent, Bien…"
                  className="mt-1 h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>

              <div>
                <Label className="text-sm text-gray-700">Note minimum *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="20"
                  value={formData.note_min}
                  onChange={(e) => setFormData({ ...formData, note_min: e.target.value })}
                  placeholder="0.00"
                  className="mt-1 h-9 rounded-lg border-gray-200 text-center font-mono focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>

              <div>
                <Label className="text-sm text-gray-700">Note maximum *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="20"
                  value={formData.note_max}
                  onChange={(e) => setFormData({ ...formData, note_max: e.target.value })}
                  placeholder="20.00"
                  className="mt-1 h-9 rounded-lg border-gray-200 text-center font-mono focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm text-gray-700">Couleur d'affichage</Label>
              <div className="mt-1 grid grid-cols-6 gap-2">
                {COULEURS_DISPONIBLES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, couleur: c.value })}
                    aria-label={c.label}
                    aria-pressed={formData.couleur === c.value}
                    className={`flex flex-col items-center gap-1.5 rounded-lg border-2 p-2 transition-all ${
                      formData.couleur === c.value
                        ? 'border-gray-800 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <div className={`h-6 w-6 rounded-lg ${c.dot}`} />
                    <span className="text-[9px] font-medium leading-none text-gray-500">{c.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Aperçu
                </span>
                <span className={`rounded-full px-4 py-1.5 text-sm font-semibold ${formData.couleur}`}>
                  {formData.libelle || 'Libellé'}
                </span>
                <span className="font-mono text-[11px] text-gray-400">
                  {formData.note_min || '?'} — {formData.note_max || '?'} / 20
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Ordre
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.ordre}
                  onChange={(e) => setFormData({ ...formData, ordre: parseInt(e.target.value, 10) || 0 })}
                  className="h-9 w-16 rounded-lg border-gray-200 text-center font-mono focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4 sm:justify-end sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowForm(false)}
              disabled={saving}
              className="h-9 min-w-[168px] justify-center gap-2"
            >
              <X className="h-4 w-4" />
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="h-9 min-w-[168px] justify-center gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:bg-gray-300"
            >
              {saving ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving ? 'Enregistrement…' : editingItem ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default ConfigAppreciations;